import { authenticatedFetch } from "./authenticatedFetch";
import { formatApiErrorMessage } from "../utils/apiErrorMessage";
import {
  isHtmlApiResponse,
  parseApiResponseBody,
} from "../utils/parseApiResponseBody";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

class LicenseService {

    async generatePaymentOrder(payload) {
        console.log('generatePaymentOrder payload:', payload);

        const response = await authenticatedFetch(
            `${API_BASE_URL}/create-payment-request`,
            {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            }
        );

        let data;

        try {
            data = await response.json();
        } catch (e) {
            throw new Error('Resposta inválida do servidor');
        }

        // Lambda-style error handling
        if (!response.ok) {
            const errorMessage =
                data?.error ||
                data?.message ||
                'Erro ao processar pagamento';

            const error = new Error(errorMessage);
            error.details = data?.details;
            error.statusCode = response.status;

            throw error;
        }

        return data;
    }

    async createSubscription(payload) {
        const response = await authenticatedFetch(
            `${API_BASE_URL}/create-subscription`,
            {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            }
        );

        return this._parseJsonResponse(response, 'Erro ao criar assinatura');
    }

    async changeSubscriptionPlan(payload) {
        const response = await authenticatedFetch(
            `${API_BASE_URL}/change-subscription-plan`,
            {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            }
        );

        return this._parseJsonResponse(response, 'Erro ao trocar plano da assinatura');
    }

    async cancelSubscription() {
        const response = await authenticatedFetch(
            `${API_BASE_URL}/cancel-subscription`,
            {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            }
        );

        return this._parseJsonResponse(response, 'Erro ao cancelar assinatura');
    }

    async getMySubscription() {
        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/get-my-subscription`, {
                method: 'GET',
                credentials: 'include',
            });

            const data = await parseApiResponseBody(response);

            if (!response.ok || isHtmlApiResponse(response, data)) {
                return {
                    success: false,
                    error: formatApiErrorMessage(
                        data,
                        'Não foi possível carregar a assinatura.'
                    ),
                };
            }

            return { success: true, ...data };
        } catch (error) {
            console.error('getMySubscription error:', error);
            return {
                success: false,
                error: formatApiErrorMessage(
                    error,
                    'Não foi possível carregar a assinatura.'
                ),
            };
        }
    }

    async _parseJsonResponse(response, defaultErrorMessage) {
        const data = await parseApiResponseBody(response);

        if (!response.ok || isHtmlApiResponse(response, data)) {
            const errorMessage = formatApiErrorMessage(data, defaultErrorMessage);
            const error = new Error(errorMessage);
            error.details = data?.details;
            error.statusCode = response.status;
            throw error;
        }

        return data;
    }

    async getMercadoPagoPublicKey() {
        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/get-public-key`, {
                method: 'GET',
                credentials: 'include',
            });

            const data = await parseApiResponseBody(response);

            if (!response.ok || isHtmlApiResponse(response, data)) {
                return {
                    success: false,
                    error: formatApiErrorMessage(
                        data,
                        'Não foi possível carregar a chave pública.'
                    ),
                };
            }

            return {
                success: true,
                data,
            };
        } catch (error) {
            console.error('getMercadoPagoPublicKey error:', error);

            return {
                success: false,
                error: formatApiErrorMessage(
                    error,
                    'Não foi possível carregar a chave pública.'
                ),
            };
        }
    }

    async getPurchases() {
        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/get-purchases`, {
                method: 'GET',
                credentials: 'include',
            });

            const data = await parseApiResponseBody(response);

            if (!response.ok || isHtmlApiResponse(response, data)) {
                return {
                    success: false,
                    error: formatApiErrorMessage(
                        data,
                        'Não foi possível carregar as compras.'
                    ),
                };
            }

            return {
                success: true,
                data,
            };
        } catch (error) {
            console.error('getPurchaseHistory error:', error);

            return {
                success: false,
                error: formatApiErrorMessage(
                    error,
                    'Não foi possível carregar as compras.'
                ),
            };
        }
    }

    async shareLicenseWithUser(payload) {
        console.log('shareLicenseWithUser payload:', payload);

        const response = await authenticatedFetch(
            `${API_BASE_URL}/licenses/invite`,
            {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            }
        );

        let data;

        try {
            data = await response.json();
        } catch (e) {
            throw new Error('Resposta inválida do servidor');
        }

        // Lambda-style error handling
        if (!response.ok) {
            const errorMessage =
                data?.error ||
                data?.message ||
                'Erro ao processar convite para usuario';

            const error = new Error(errorMessage);
            error.details = data?.details;
            error.statusCode = response.status;

            throw error;
        }

        return data;
    }

    async handleLicenseResponse(payload) {
        console.log('handleLicenseResponse payload:', payload);

        const response = await authenticatedFetch(
            `${API_BASE_URL}/licenses/handle-invitation`,
            {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            }
        );

        let data;

        try {
            data = await response.json();
        } catch (e) {
            throw new Error('Resposta inválida do servidor');
        }

        // Lambda-style error handling
        if (!response.ok) {
            const errorMessage =
                data?.error ||
                data?.message ||
                'Erro ao processar convite para usuario';

            const error = new Error(errorMessage);
            error.details = data?.details;
            error.statusCode = response.status;

            throw error;
        }

        return data;
    }


    async removeMember(payload) {
        console.log('removeUserFromLicense payload:', payload);

        const response = await authenticatedFetch(
            `${API_BASE_URL}/licenses/remove-member`,
            {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            }
        );

        let data;

        try {
            data = await response.json();
        } catch (e) {
            throw new Error('Resposta inválida do servidor');
        }

        if (!response.ok) {
            const errorMessage =
                data?.error ||
                data?.message ||
                'Erro ao processar remocao do usuario';

            const error = new Error(errorMessage);
            error.details = data?.details;
            error.statusCode = response.status;

            throw error;
        }

        return data;
    }

}

export default new LicenseService();